import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

export const useDeleteImpactTRPC = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.impact.delete.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impact.impacts.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impact.impactById.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impact.impactList.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impact.impactCount.queryKey(),
          }),
          // Cascade delete removes ratings too
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impactRating.impactRatings.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.impactRating.impactRatingsByImpactId.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.impactRating.impactRatingsByRatedItemId.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.impactRating.impactRatingsWithAppetites.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impactRating.impactRatingCount.queryKey(),
          }),
        ]);
      },
    }),
    throwOnError: true,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && mutation.error) {
      addNotification({
        type: 'error',
        content: mutation.error.message,
      });
    }
  }, [trpcEnabled, mutation.error, addNotification]);

  return {
    deleteImpact: async (variables: { Id: string }): Promise<void> => {
      await mutation.mutateAsync({ Id: variables.Id });
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
